import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { initializeSocket } from "../../components/comments/socketService";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";

import { LoginCredentials } from "../../api/types/auth/authTypes";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { fetchCurrentUser } from "../../store/slices/misc/currentUser";
import authService from "../../api/auth/authService";
import tokenStore from "../../auth/tokenStore";
import { SESSION_EXPIRED_MESSAGE_KEY } from "../../auth/performSignOut";
import axios from "axios";
import { resetStore } from "../../store/slices/projectManagement/GlobalStoreUpdate";
import AuthShell from "./AuthShell";

// makeApiCall (api/master/axiosIndex) swallows axios errors and hands the error
// object back, so a rejected login arrives either as the gateway's JSON body
// ({status:false, message:"Invalid credentials"}) or as an AxiosError wrapping
// it. Pull the server's own message out of both shapes — never show axios's
// "Request failed with status code 401" to a user.
const loginErrorMessage = (result: any): string => {
  const serverMessage =
    result?.response?.data?.message ?? result?.data?.message ?? result?.message;
  if (
    typeof serverMessage === "string" &&
    serverMessage.trim() &&
    !serverMessage.startsWith("Request failed with status code")
  ) {
    return serverMessage;
  }
  return "Unable to sign in. Please check your credentials and try again.";
};

const Login: React.FC<any> = ({ setIsLoggedIn }) => {
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [passState, setPassState] = useState<boolean>(false);
  const [errorVal, setError] = useState<string>("");

  // Surface "Your session has expired..." after an expiry-driven redirect
  // (set by performSignOut / the interceptor) instead of a silent bounce.
  useEffect(() => {
    const expiredMessage = sessionStorage.getItem(SESSION_EXPIRED_MESSAGE_KEY);
    if (expiredMessage) {
      sessionStorage.removeItem(SESSION_EXPIRED_MESSAGE_KEY);
      setError(expiredMessage);
    }
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>();

  const onFormSubmit = async (formData: LoginCredentials) => {
    setError("");
    setLoading(true);
    try {
      const data = await authService.login({
        ...formData,
        applicationType: "Buyer",
      });

      if (data.status && data.token && data.token.access_token) {
        // Cookie lifetime + session timers come from the login response.
        tokenStore.setTokens(data.token, data);
        // Store login method for app switching
        localStorage.setItem("loginMethod", "regular");
        const ipAddress = await getIpAddress();
        const resultAction = await dispatch(fetchCurrentUser());
        const currentUser = resultAction.payload;

        if (!currentUser?.id) {
          throw new Error("User ID not available after login");
        }

        authService.createLoginActivity({
          deviceInfo: getBrowserInfo(),
          ipAddress,
          loginDateTime: new Date().toISOString(),
        });

        const storedClientId = uuidv4();
        sessionStorage.setItem("clientId", storedClientId);

        console.log("login file:", {
          userId: currentUser.id,
          clientId: storedClientId,
          token: data.token.access_token,
        });

        initializeSocket(
          currentUser.id.toString(),
          storedClientId,
          data.token.access_token,
          () => console.log("Socket connected"),
          () => console.log("Socket disconnected")
        );

        // Store payment due alerts to show after navigation
        if (data.alerts && data.alerts.length > 0) {
          const paymentAlert = data.alerts.find(
            (a: any) => a.type === "payment_due"
          );
          if (paymentAlert) {
            sessionStorage.setItem(
              "pending_payment_alert",
              JSON.stringify(paymentAlert)
            );
          }
        }

        navigate("/pms/projects");

        setLoading(false);
        setIsLoggedIn(true);
      } else {
        setError(loginErrorMessage(data));
        setLoading(false);
      }
    } catch (err: any) {
      console.log("Login failed:", err?.message);
      setError(loginErrorMessage(err));
      setLoading(false);
    }
  };

  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    const browserMapper = {
      Edg: "Microsoft Edge",
      OPR: "Opera",
      Vivaldi: "Vivaldi",
      SamsungBrowser: "Samsung Internet",
      QQBrowser: "QQ Browser",
      UCBrowser: "UC Browser",
      YaBrowser: "Yandex Browser",
      Silk: "Amazon Silk",
      Maxthon: "Maxthon",
      Brave: "Brave",
      Chrome: "Chrome",
      Safari: "Safari",
      Firefox: "Firefox",
      MSIE: "Internet Explorer",
      "Trident/": "Internet Explorer",
      IEMobile: "Internet Explorer",
      Opera: "Opera",
      Netscape: "Netscape",
      SeaMonkey: "SeaMonkey",
      Konqueror: "Konqueror",
      "Firefox Focus": "Firefox Focus",
      Iceweasel: "Firefox",
    };

    // Default to unknown
    let browserName = "Unknown";

    // Check each key in the mapper object
    for (const key in browserMapper) {
      if (userAgent.indexOf(key) > -1) {
        browserName = browserMapper[key as keyof typeof browserMapper];
        break;
      }
    }
    return browserName;
  };

  const getIpAddress = async () => {
    try {
      const response: any = await axios.get(
        "https://api.ipify.org?format=json"
      );
      const data = response.data;
      return data.ip;
    } catch (error) {
      console.error("Error fetching IP address: ", error);
    }
  };

  useEffect(() => {
    resetStore(dispatch);
  }, []);

  return (
    <AuthShell
      title="Login"
      heading="Let's build."
      subheading="Enter your credentials to access your account."
    >
      {errorVal && (
        <div className="pxauth__alert" role="alert">
          <ExclamationCircleFilled
            className="pxauth__alertIcon"
            aria-hidden="true"
          />
          <span className="pxauth__alertText">{errorVal}</span>
        </div>
      )}

      <form
        className="pxauth-form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
      >
        <div className="pxauth-field">
          <div className="pxauth-labelRow">
            <label className="pxauth-label" htmlFor="pxauth-email">
              Email address
            </label>
          </div>
          <div className="pxauth-control">
            <input
              id="pxauth-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoFocus
              placeholder="Enter your email address"
              className={`pxauth-input${errors.email ? " is-invalid" : ""}`}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "pxauth-email-error" : undefined}
              {...register("email", { required: "This field is required" })}
            />
          </div>
          {errors.email && (
            <p className="pxauth-error" id="pxauth-email-error" role="alert">
              {errors.email.message}
            </p>
          )}
        </div>

        <div className="pxauth-field">
          <div className="pxauth-labelRow">
            <label className="pxauth-label" htmlFor="pxauth-password">
              Password
            </label>
            <Link
              className="pxauth-forgot"
              to={`${process.env.PUBLIC_URL}/auth-reset`}
            >
              Forgot password?
            </Link>
          </div>
          <div className="pxauth-control pxauth-control--password">
            <input
              id="pxauth-password"
              type={passState ? "text" : "password"}
              autoComplete="current-password"
              placeholder="Enter your passcode"
              className={`pxauth-input${errors.password ? " is-invalid" : ""}`}
              aria-invalid={!!errors.password}
              aria-describedby={
                errors.password ? "pxauth-password-error" : undefined
              }
              {...register("password", { required: "This field is required" })}
            />
            <button
              type="button"
              className="pxauth-reveal"
              aria-label={passState ? "Hide password" : "Show password"}
              aria-pressed={passState}
              aria-controls="pxauth-password"
              onClick={() => setPassState((prev) => !prev)}
            >
              {passState ? (
                <EyeInvisibleOutlined aria-hidden="true" />
              ) : (
                <EyeOutlined aria-hidden="true" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="pxauth-error" id="pxauth-password-error" role="alert">
              {errors.password.message}
            </p>
          )}
        </div>

        <button type="submit" className="pxauth-btn" disabled={loading}>
          {loading ? (
            <>
              <span className="pxauth-spinner" aria-hidden="true" />
              Signing in&hellip;
            </>
          ) : (
            "Sign in"
          )}
        </button>
      </form>
    </AuthShell>
  );
};

export default Login;
